import { useEffect, useState } from 'react';
import { SCREENS } from '~/constants/screens';
import { localeStore } from '~/mobx/localesStore';
import { history } from '~/services/history';
import { TBooleanInNumber } from '~/types/main';
import { booleanToNumber } from '~/utils/helpers';
import marketplaceStore from '~/view/screens/MarketplaceScreen/store/marketplaceStore';

type WalletSettingsInitialState = {
  walletCurrencyEn: string;
  walletCurrencyAr: string;
  walletCurrencyId: string;
  walletCurrency: string;
  isWalletUsageLimit: TBooleanInNumber | undefined;
  walletUsageLimit: string | undefined;
  enabled: boolean;
};

export const useWalletSettings = ({
  walletCurrencyEn,
  walletCurrencyAr,
  walletCurrencyId,
  walletCurrency,
  isWalletUsageLimit,
  walletUsageLimit,
  enabled,
}: WalletSettingsInitialState) => {
  const [currencyName, setCurrencyName] = useState({ walletCurrencyEn, walletCurrencyAr });
  const [currencyCode, setCurrencyCode] = useState({ walletCurrencyId, walletCurrency });
  const [isLimit, setIsLimit] = useState<TBooleanInNumber>(isWalletUsageLimit ?? 0);
  const [limitValue, setLimitValue] = useState<string>(walletUsageLimit ?? '');
  const [validationErrors, setValidationErrors] = useState({ walletUsageLimit: '' });
  const [status, setStatus] = useState(enabled);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!status) {
      setIsLimit(0);
    }
  }, [status]);

  const handleCurrencyNameChange = (field: string) => (name: string | undefined) => {
    setCurrencyName({ ...currencyName, [field]: name });
    marketplaceStore.setUnsavedData(true);
  };

  const handleCurrencySelect = (id: string, code: string) => {
    setCurrencyCode({ walletCurrencyId: id, walletCurrency: code });
    marketplaceStore.setUnsavedData(true);
  };

  const handleLimitToggle = (status: TBooleanInNumber) => {
    setIsLimit(status);
    marketplaceStore.setUnsavedData(true);
  };

  const handleWalletUsageLimitChange = (value: string | undefined) => {
    if (value === '0' || (value && value.length > 2)) return;
    setLimitValue(value ? value.replace(/[^0-9]/g, '') : '');
    marketplaceStore.setUnsavedData(true);
  };

  const handleStatusChange = (status: TBooleanInNumber) => {
    setStatus(Boolean(status));
    marketplaceStore.setUnsavedData(true);
  };

  const handleSaveData = async () => {
    if (isLimit && !limitValue) {
      setValidationErrors({ walletUsageLimit: localeStore.t('required_field_error') });
      return;
    }

    setLoading(true);

    await marketplaceStore.updateMarketplaceFeature({
      wallet_currency_ar: currencyName.walletCurrencyAr || undefined,
      wallet_currency_en: currencyName.walletCurrencyEn || undefined,
      wallet_addon_currency_id: currencyCode.walletCurrencyId,
      is_wallet_usage_limit: status ? isLimit : 0,
      wallet_usage_limit: isLimit ? Number(limitValue) : null,
      wallet: booleanToNumber(status),
    });

    setLoading(false);
    marketplaceStore.setUnsavedData(false);

    history.push(SCREENS.MARKETPLACE());
  };

  return {
    currencyName,
    currencyCode,
    isLimit,
    limitValue,
    validationErrors,
    status,
    loading,
    handleCurrencyNameChange,
    handleCurrencySelect,
    handleLimitToggle,
    handleWalletUsageLimitChange,
    handleStatusChange,
    handleSaveData,
  };
};
